import axios from "axios";
import { constructAndReturnValidationErrorMessage } from "./PreviewUtils";

const APIENDPOINT = process.env.REACT_APP_HOST;

export const fetchCampaignData = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/campaigns`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    switch (response.status) {
      case 200:
        return { success: true, data: response.data };

      case 403:
        return {
          success: false,
          message: response.data?.message || "You don't have permission to access campaigns.",
        };

      case 400:
      case 404:
      case 500:
        return {
          success: false,
          message: response.data?.message || "Failed to fetch campaigns.",
        };

      default:
        return { success: false, message: "An unexpected error occurred." };
    }
  } catch (error) {
    return { success: false, message: "An error occurred while fetching campaigns." };
  }
};

export const fetchExistingCampaigns = async (bearerToken) => {
  let message;
  try {
    const response = await axios.get(`https://${APIENDPOINT}/campaigns`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status < 500,
    });

    switch (response.status) {
      case 200:
        const data = await response.data;
        return {
          success: true,

          data: data.records,
        };
      case 403:
        return {
          success: false,
          error: response.data?.message,
        };

      case 404:
        message = "No campaigns exist for this user currently";

        return {
          success: false,
          error: message,
        };

      default:
        message =
          "Something went wrong while attempting to display data on the screen.";
        return {
          success: false,
          error: message,
        };
    }
  } catch (error) {
    message =
      "Something went wrong while attempting to display data on the screen.";
    return {
      success: false,
      error: message,
    };
  }
};
export const createCampaign = async (payload, bearerToken) => {
  const response = await axios.post(
    `https://${APIENDPOINT}/campaigns`,
    payload,
    {
      headers: {
        Authorization: bearerToken,
        "Content-Type": "application/json",
      },
      validateStatus: (status) => status <= 500,
    }
  );
  return response;
};

export const uploadContactsToAPI = async (csvFile, payload, bearerToken) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) =>
    formData.append(key, value)
  );
  formData.append("file", csvFile);

  try {
    const response = await axios.post(`https://${APIENDPOINT}/bulk`, formData, {
      headers: {
        Authorization: bearerToken,
        "Content-Type": "multipart/form-data",
      },
      validateStatus: (status) => status <= 500,
    });

    if (response.status === 200) {
      return {
        success: true,
        message: "Contacts uploaded successfully",
        data: response.data,
      };
    }
    else if (response.status === 403) {
      if (response?.data?.details) {
        return {
          success: false,
          message: `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}, Requested Count ${response.data.details.incoming_request_data_length}.\nContact Support to increase your Quota.`
        }
      } else {
        return {
          success: false,
          message: response?.data?.message
        }
      }
    }
    return {
      success: false,
      message: response.data?.message || "Unexpected error occurred",
    };
  } catch (err) {
    return {
      success: false,
      message: "Upload failed. Please contact support.",
    };
  }
};

const processGroup = (groupData, groupName, formData) => {
  if (!groupData) return;

  const jsonCopy = {};
  const filesInGroup = {};

  Object.entries(groupData).forEach(([key, value]) => {
    if (value instanceof File) {
      filesInGroup[key] = value;
    } else {
      jsonCopy[key] = value;
    }
  });

  // append JSON
  formData.append(groupName, JSON.stringify(jsonCopy));

  // append files under same group namespace
  Object.entries(filesInGroup).forEach(([key, file]) => {
    formData.append(key, file);
  });
};

export const saveCustomizationToDB = async (data, bearerToken) => {
  try {
    const formData = new FormData();
    formData.append("template", data.template);
    formData.append("name", data.name);
    formData.append("owner", data.owner);

    processGroup(data.contact, "contact", formData);
    processGroup(data.customer, "customer", formData);

    const response = await axios.post(
      `https://${APIENDPOINT}/templates`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};
export const getCustomizedTemplatesCreatedByUser = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/templates`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status < 500,
    });
    switch (response.status) {
      case 200:
        return response.data.records;
      case 404:
        return [];
      default:
        return [];
    }
  } catch (error) {
    let message = 'The operation you attempted has failed.Please contact support.'
    return message
  }
};

export const fetchTemplatesConfig = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/template-configs`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    return response;
  } catch {
    let message = "The operation you attempted has failed. Please contact support.";
    return message;
  }
};

export const fetchTemplateConfigDetails = async (owner, name, bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/template-configs`, {
      params: {
        name: name,
        owner: owner,
      },
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    return response;
  } catch (error) {
    return {
      status: 500,
      message: "The operation you attempted has failed. Please contact support.",
    };
  }
};
export const requestForPDFGeneration = async (
  campaign,
  template,
  orderType,
  bearerToken
) => {
  try {
    const response = await axios.post(
      `https://${APIENDPOINT}/requests`,
      {
        campaign: campaign,
        template: template,
        delivery_method: orderType,
      },

      {
        headers: {
          Authorization: bearerToken,
        },
        timeout: 30000,
        validateStatus: (status) => status < 500,
      }
    );
    switch (response.status) {
      case 201:
        return {
          success: true,
          message: "Your request to 'Generate Print Collaterals' is accepted.\nIt will take some time to process your request.\nYou can refresh the grid to see the status of your request.",
          data: response.data,
        };
      case 403: {
        let errorMessage;
        if (response.data.details) {
          if (response.data.details.action) {
            errorMessage = `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}, Requested Count ${response.data.details.incoming_request_data_length}.\nContact Support to increase your Quota.`
          } else {
            const extraCreditInfo = response.data.details.extra_credit_limit !== 0 ? `Extra credit limit ${response.data.details.extra_credit_limit}, Extra credit used ${response.data.details.extra_credit_used}, ` : "";
            errorMessage = `Insufficient credits: Credit limit ${response.data.details.credit_limit}, Credit used ${response.data.details.credit_used}, ${extraCreditInfo}Available credit ${response.data.details.credit_available}, Credit required ${response.data.details.credit_required}.\nContact Support to buy extra credits.`;
          }
          return {
            success: false,
            error: errorMessage
          }
        } else if (response.data.message) {
          return {
            success: false,
            error: response.data.message
          }
        }
        break;
      }
      case 422:
        return {
          success: false,
          error: response.data.message
        }
      case 409:
        return {
          success: false,
          error:
            "Request with the same values is already created. Please download the file from the grid when it is complete",
        };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const fetchPdfGenerationRequestsForCustomer = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/requests`, {
      headers: {
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });

    const data = await response.data;

    switch (response.status) {
      case 200:
        return {
          success: true,
          data: data,
        };
      case 404:
        return {
          success: true,
          data: data,
        };
      case 500:
        return {
          success: false,
          error: "The operation you attempted has failed. Please contact support.",
        };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const fetchCampaigns = async (BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/campaigns`, {
    headers: {
      Authorization: BEARER,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const fetchRespondedContacts = async (campaignName, BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/records`, {
    headers: {
      Authorization: BEARER,
    },
    params: {
      campaign: campaignName,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const fetchContactVisits = async (trackable, BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/related`, {
    params: {
      path: trackable,
    },
    headers: {
      Authorization: BEARER,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const fetchUsersFromDB = async (BEARER) => {
  return await axios.get(`https://${APIENDPOINT}/users`, {
    headers: {
      Authorization: BEARER,
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });
};

export const createUser = async (payload, BEARER) => {
  try {
    const action = "CREATE_USER";
    const response = await axios.post(
      `https://${APIENDPOINT}/users`,
      { ...payload, action },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: BEARER,
        },
        validateStatus: (status) => status >= 200 && status <= 500,
      }
    );
    if (response.status === 201) {
      const data = response.data;
      return {
        success: true,
        message: `An account for email ${data.details.email} has been created successfully. Associated customer ID is ${data.details.customer_id}`,
      };
    } else if (response.status === 400) {
      return {
        success: false,
        message: 'Invalid request payload. Please check the inputs provided and try again.',
      };
    } else if (response.status === 409) {
      return {
        success: false,
        message: `User with email ${payload.email} already exists. Please use a different email address.`,
      };
    } else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message,
      }
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: `The operation you attempted has failed. Please contact support.`,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: `A network request exception occurred`,
    };
  }
};

export const sendEmail = async (bearerToken, email, sub) => {
  try {
    const action = "RESEND_ONBOARDING_EMAIL";
    const response = await axios.post(`https://${APIENDPOINT}/users`,
      {
        email: email,
        sub: sub,
        action: action
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      })
    const data = response.data;
    if (response.status === 200) {
      return {
        success: true,
        message: `${data.message} ${email}`,
      }
    } else if (response.status === 400) {
      return {
        success: false,
        message: data,
      }
    } else if (response.status === 404) {
      return {
        success: false,
        message: `User with email ${email} does not exist.`,
      }
    }
    else if (response.status === 409) {
      return {
        success: false,
        message: data.message,
      }
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: data.message,
      }
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: `The operation you attempted has failed. Please contact support.`,
      }
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,

    }
  }
}
export const generatePreviewPdf = async (data, BEARER) => {
  try {
    const action = "PREVIEW";
    const formData = new FormData();

    formData.append("name", data.name);
    formData.append("owner", data.owner);
    formData.append("action", action);

    processGroup(data.contact, "contact", formData);
    processGroup(data.customer, "customer", formData);

    const response = await axios.post(
      `https://${APIENDPOINT}/preview`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: BEARER,
        },
        responseType: "arraybuffer",
        validateStatus: (status) => status >= 200 && status <= 500,
      }
    );
    if (response.status === 200) {
      const data = response.data;

      return {
        success: true,
        data: data
      };
    } else if (response.status === 400) {
      const decoder = new TextDecoder("utf-8");
      const responseText = decoder.decode(response.data);
      const responseJson = JSON.parse(responseText);
      if (responseJson.errors && responseJson.errors.length > 0) {
        const errorMessage = responseJson.errors[0].qrCodeContent;
        return {
          success: false,
          message: errorMessage,
        };
      }
      return {
        success: false,
        message: responseJson.message || "The request was invalid. Please check your inputs.",
      };

    } else if (response.status === 403) {
      const responseJson = JSON.parse(new TextDecoder().decode(response.data));
      return {
        success: false,
        message: responseJson.message,
      }
    }
    else if (response.status === 422) {
      const responseJson = JSON.parse(new TextDecoder().decode(response.data));
      return {
        success: false,
        message: constructAndReturnValidationErrorMessage(responseJson),
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message:
          "The operation you attempted has failed. Please contact support.",
      };
    }
  } catch (error) {
    return {
      success: false,
      message:
        "The operation you attempted has failed. Please contact support.",
    };
  }
};

export const viewPreviewPdf = async (formData, BEARER) => {
  try {
    const action = "VIEW";
    const response = await axios.post(
      `https://${APIENDPOINT}/preview`,
      { ...formData, action: action },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: BEARER,
        },
        responseType: "arraybuffer",
        validateStatus: (status) => status >= 200 && status <= 500,
      }
    );
    if (response.status === 200) {
      const data = response.data;

      return {
        success: true,
        data: data,
      };
    } else if (response.status === 400) {
      const responseJson = response.data;
      if (responseJson.errors && responseJson.errors.length > 0) {
        const errorMessage = responseJson.errors[0].qrCodeContent;
        return {
          success: false,
          message: errorMessage,
        };
      }
      return {
        success: false,
        message: responseJson.message || "The request was invalid. Please check your inputs.",
      };
    } else if (response.status === 403) {
      const responseJson = JSON.parse(new TextDecoder().decode(response.data));
      return {
        success: false,
        message: responseJson.message,
      }
    }
    else if (response.status === 422) {
      const responseJson = JSON.parse(new TextDecoder().decode(response.data));
      return {
        success: false,
        message: constructAndReturnValidationErrorMessage(responseJson),
      };
    } else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || "Template not found.",
      };
    } else if (response.status === 500) {
      return {
        success: false,
        message:
          "The operation you attempted has failed. Please contact support.",
      };
    }
  } catch (error) {
    return {
      success: false,
      message:
        "The operation you attempted has failed. Please contact support.",
    };
  }
};

export const editUserPartners = async (bearerToken, sub, partners) => {
  try {
    const response = await axios.patch(`https://${APIENDPOINT}/users/${sub}`,
      {
        partners: partners,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      })
    const data = response.data;
    if (response.status === 200) {
      return {
        success: true,
        message: data.message || `User updated successfully`,
      }
    }
    else if (response.status === 400) {
      return {
        success: false,
        message: data.message || `Failed to update user`,
      }
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: data.message || `User not found`,
      }
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: data.message || `Invalid inputs`,
      }
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: data.message || `The operation you attempted has failed. Please contact support.`,
      }
    } else {
      return {
        success: false,
        message: data.message || `The operation you attempted has failed. Please contact support.`,
      }
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    }
  }
}

export const fetchAdminActivityLogs = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/activity-logs`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    return response;
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    }
  }
}


export const updatePdfRequestStatus = async (bearerToken, uniqueId, customerId, newStatus) => {
  try {
    const response = await axios.patch(
      `https://${APIENDPOINT}/requests/${uniqueId}`,
      {
        request_status: newStatus,
        customer_id: customerId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      }
    );
    const data = response.data;
    if (response.status === 200) {
      return {
        success: true,
        message: data.message || `Status updated successfully`,
      };
    }
    else if (response.status === 400) {
      return {
        success: false,
        message: data.message || `Failed to update status`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: data.message || `Request not found`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: data.message || `Invalid status`,
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: data.message || `Failed to update PDF request status`,
      };
    }
    else {
      return {
        success: false,
        message: data.message || `Failed to update status`,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
};

export const fetchSubscriptionPlans = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/subscription-plans`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch plans`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Plans not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch plans`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
};

export const createSubscriptionPlan = async (bearerToken, payload) => {
  try {
    const response = await axios.post(`https://${APIENDPOINT}/subscription-plans`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      });
    if (response.status === 201) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to create plan`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Plans not found`,
      };
    }
    else if (response.status === 409) {
      return {
        success: false,
        message: response.data.message || `Plan Already Existed`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message || `Invalid inputs`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to create plan`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }

}

export const fetchSubscriptions = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/subscriptions`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscriptions not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}


export const createSubscription = async (bearerToken, payload) => {
  try {
    const response = await axios.post(`https://${APIENDPOINT}/subscriptions`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      });
    if (response.status === 201) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to create plan`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Plans not found`,
      };
    }
    else if (response.status === 409) {
      return {
        success: false,
        message: response.data.message || `Plan Already Existed`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message || `Invalid inputs`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to create plan`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }

};


export const activateSubscription = async (bearerToken, subscription_id, payload) => {
  try {
    const response = await axios.post(`https://${APIENDPOINT}/subscriptions/${subscription_id}/actions`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to activate subscription`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscription not found`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message || `Invalid inputs`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to activate subscription`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const suspendSubscription = async (bearerToken, subscription_id, payload) => {
  try {
    const response = await axios.post(`https://${APIENDPOINT}/subscriptions/${subscription_id}/actions`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to activate subscription`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscription not found`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message || `Invalid inputs`,
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: response.data.message || `Failed to activate subscription`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to activate subscription`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const changePlanAndBillingCycle = async (bearerToken, subscription_id, payload) => {
  try {
    const response = await axios.post(`https://${APIENDPOINT}/subscriptions/${subscription_id}/actions`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400) {
      let message = "";
      if (payload?.action === "renew") {
        message = "Failed to renew Subsctiption";
      }
      else if (payload?.action === "upgrade") {
        message = "Failed to upgrade Subsctiption";
      }
      return {
        success: false,
        message: response.data.message || message,
      };
    }
    else if (response.status === 404) {

      return {
        success: false,
        message: response.data.message || `Subscription not found`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message || `Invalid inputs`,
      };
    }
    else if (response.status === 500) {
      let message = "";
      if (payload?.action === "renew") {
        message = "Failed to renew Subsctiption";
      }
      else if (payload?.action === "upgrade") {
        message = "Failed to upgrade Subsctiption";
      }
      return {
        success: false,
        message: response.data.message || message,
      };
    }
    else {
      let message = "";
      if (payload?.action === "renew") {
        message = "Failed to renew Subsctiption";
      }
      else if (payload?.action === "upgrade") {
        message = "Failed to upgrade Subsctiption";
      }
      return {
        success: false,
        message: response.data.message || message,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }

};

export const addExtraCredits = async (bearerToken, subscription_id, payload) => {
  try {
    const response = await axios.post(`https://${APIENDPOINT}/subscriptions/${subscription_id}/actions`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: bearerToken,
        },
        validateStatus: (status) => status <= 500,
      });
    if (response.status === 201) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400) {
      return {
        success: false,
        message: response.data.message || `Failed to add extra credits`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscription not found`,
      };
    }
    else if (response.status === 422) {
      return {
        success: false,
        message: response.data.message || `Invalid inputs`,
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: response.data.message || `Failed to add extra credits`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to add extra credits`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }

};


export const fetchUsageMetricsPerCustomer = async (bearerToken, customer_id) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/usage-metrics/${customer_id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscriptions not found`,
      };
    }
    else if (response.status === 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}


export const fetchExtraCreditsPerCustomer = async (bearerToken, customer_id) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/extra-credits`, {
      params: {
        customer_id: customer_id
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscriptions not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscriptions`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const fetchUserProfile = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/user`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch user profile`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `User profile not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch user profile`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const fetchUserActivityLogs = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/activities`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch user activity logs`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `User activity logs not found`,
      };
    }

    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch user activity logs`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const fetchSubscription = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/subscriptions`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscription`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscription not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch subscription`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const fetchExtraCredits = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/extra-credits`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch extra credits`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Extra credits not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch extra credits`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}


export const fetchUsageMetric = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/usage-metrics`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch usage metrics`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Usage metrics not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch usage metrics`,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}


export const fetchPayments = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/payments`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch payments`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Payments not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch payments`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}

export const updateSubscription = async (bearerToken, subscription_id, payload) => {
  try {
    const response = await axios.put(`https://${APIENDPOINT}/subscriptions/${subscription_id}`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 204) {
      return {
        success: true,
        message: "Subscription updated successfully",
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to update subscription`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Subscription not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to update subscription`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}


export const extendQuota = async (bearerToken, subscription_id, payload) => {
  try {
    const response = await axios.patch(`https://${APIENDPOINT}/subscriptions/${subscription_id}/quota-limits`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to extend quota`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Quota not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to extend quota`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
}



export const fetchPlansForCustomer = async (bearerToken) => {
  try {
    const response = await axios.get(`https://${APIENDPOINT}/plans`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: bearerToken,
      },
      validateStatus: (status) => status <= 500,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    else if (response.status === 400 || 500) {
      return {
        success: false,
        message: response.data.message || `Failed to fetch plans`,
      };
    }
    else if (response.status === 404) {
      return {
        success: false,
        message: response.data.message || `Plans not found`,
      };
    }
    else {
      return {
        success: false,
        message: response.data.message || `Failed to fetch plans`,
      };
    }
  } catch {
    return {
      success: false,
      message: `The operation you attempted has failed. Please contact support.`,
    };
  }
};
